import { LegacyChainClient } from '../core/LegacyChainClient';
import { PlanType } from '../types';

export interface MultiSigProposal {
  id: string;
  capsuleId: string;
  requiredSignatures: number;
  signers: string[];
  signatureCount: number;
  executed: boolean;
  expiryTime: Date;
  data: any;
  signatures: Array<{
    signer: string;
    signedAt: Date;
  }>;
}

export interface CreateMultiSigInput {
  capsuleId: string;
  signers: string[];
  requiredSignatures: number;
  action: 'unlock' | 'transfer' | 'update' | 'custom';
  data?: any;
  duration?: number; // hours
}

export class MultiSigService {
  constructor(private client: LegacyChainClient) {}

  /**
   * Check if multi-signature is available for the current plan
   */
  private checkMultiSigAccess() {
    const planType = this.client.getPlanType();
    if (planType === PlanType.FREE) {
      throw new Error('Multi-signature feature requires a paid plan');
    }
  }

  /**
   * Create a multi-signature proposal
   */
  async createProposal(input: CreateMultiSigInput): Promise<MultiSigProposal> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<MultiSigProposal>(
      'POST',
      '/multisig/proposals',
      {
        ...input,
        duration: input.duration || 72 // Default 72 hours
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create multi-sig proposal');
    }

    return response.data;
  }

  /**
   * Sign a multi-signature proposal
   */
  async signProposal(proposalId: string): Promise<MultiSigProposal> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<MultiSigProposal>(
      'POST',
      `/multisig/proposals/${proposalId}/sign`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to sign proposal');
    }

    return response.data;
  }

  /**
   * Get a specific proposal
   */
  async getProposal(proposalId: string): Promise<MultiSigProposal> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<MultiSigProposal>(
      'GET',
      `/multisig/proposals/${proposalId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get proposal');
    }

    return response.data;
  }

  /**
   * List proposals for a capsule
   */
  async listProposals(capsuleId: string, options?: {
    status?: 'pending' | 'executed' | 'expired';
    limit?: number;
    offset?: number;
  }): Promise<{
    proposals: MultiSigProposal[];
    total: number;
  }> {
    this.checkMultiSigAccess();

    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await this.client.makeRequest<any>(
      'GET',
      `/multisig/capsules/${capsuleId}/proposals?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to list proposals');
    }

    return response.data;
  }

  /**
   * Execute a proposal (if threshold is met)
   */
  async executeProposal(proposalId: string): Promise<{
    success: boolean;
    transactionHash?: string;
  }> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<any>(
      'POST',
      `/multisig/proposals/${proposalId}/execute`
    );

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to execute proposal');
    }

    return response.data;
  }

  /**
   * Cancel a proposal (only by capsule owner)
   */
  async cancelProposal(proposalId: string): Promise<boolean> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<{ success: boolean }>(
      'DELETE',
      `/multisig/proposals/${proposalId}`
    );

    return response.success;
  }

  /**
   * Add signers to a capsule
   */
  async addSigners(capsuleId: string, signers: string[]): Promise<boolean> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<{ success: boolean }>(
      'POST',
      `/multisig/capsules/${capsuleId}/signers`,
      { signers }
    );

    return response.success;
  }

  /**
   * Remove signers from a capsule
   */
  async removeSigners(capsuleId: string, signers: string[]): Promise<boolean> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<{ success: boolean }>(
      'DELETE',
      `/multisig/capsules/${capsuleId}/signers`,
      { signers }
    );

    return response.success;
  }

  /**
   * Update signature threshold
   */
  async updateThreshold(
    capsuleId: string,
    requiredSignatures: number
  ): Promise<boolean> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<{ success: boolean }>(
      'PUT',
      `/multisig/capsules/${capsuleId}/threshold`,
      { requiredSignatures }
    );

    return response.success;
  }

  /**
   * Get multi-sig configuration for a capsule
   */
  async getConfiguration(capsuleId: string): Promise<{
    signers: string[];
    requiredSignatures: number;
    activeProposals: number;
  }> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<any>(
      'GET',
      `/multisig/capsules/${capsuleId}/config`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get configuration');
    }

    return response.data;
  }

  /**
   * Batch sign multiple proposals
   */
  async batchSign(proposalIds: string[]): Promise<{
    signed: string[];
    failed: Array<{ proposalId: string; error: string }>;
  }> {
    this.checkMultiSigAccess();

    const response = await this.client.makeRequest<any>(
      'POST',
      '/multisig/proposals/batch-sign',
      { proposalIds }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to batch sign');
    }

    return response.data;
  }
} 